import React, { useState } from 'react';
import FillEditor from './FillEditor';
import { useSvgAsset } from './useSvg';
import { resolveColors } from './svgColors';
import { SOLID_PATTERN_ID } from './assetManifest';
import styles from './panelStyles';

const smallBtn = (isActive) => ({
    ...styles.optionBtn(isActive),
    padding: '7px 4px',
    fontSize: '10px',
});

// Editor for the stripe currently being edited.
function StripeEditor({
    stripe, config, patterns, bodyColors, stripeDefaults,
    onFillChange, onColorChange, onPatternColorChange,
}) {
    const patternId = config.patternId || null;
    const pattern = patternId ? patterns.find((p) => p.id === patternId) : null;

    const stripeAsset = useSvgAsset(stripe.url);
    const patternAsset = useSvgAsset(pattern ? pattern.url : null);

    const colors = pattern
        ? (patternAsset
            ? resolveColors(patternAsset.slots, (config.patternColors || {})[patternId], bodyColors)
            : null)
        : (stripeAsset ? resolveColors(stripeAsset.slots, config.colors, stripeDefaults) : null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
                <div style={styles.sectionLabel}>Fill Type</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
                    <button style={smallBtn(!patternId)} onClick={() => onFillChange(stripe.id, null)}>
                        Color
                    </button>
                    {patterns.filter((p) => p.id !== SOLID_PATTERN_ID).map((p) => (
                        <button
                            key={p.id}
                            style={smallBtn(patternId === p.id)}
                            onClick={() => onFillChange(stripe.id, p.id)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <div style={styles.divider} />

            <FillEditor
                key={`${stripe.id}:${patternId}`}
                colors={colors}
                onChange={(slot, hex) => (pattern
                    ? onPatternColorChange(stripe.id, patternId, slot, hex)
                    : onColorChange(stripe.id, slot, hex))}
            />
        </div>
    );
}

/**
 * StripePanel – toggle stripes on/off and set each one's fill (own colour(s)
 * or a pattern clipped to the stripe).
 *
 * Props:
 *   stripes, patterns, enabledIds, stripeConfigs
 *   bodyColors     – shared pattern colours (default for pattern fills)
 *   stripeDefaults – default colour per slot for solid stripes
 *   onToggle(stripeId)
 *   onFillChange(stripeId, patternId | null)
 *   onColorChange(stripeId, slot, hex)
 *   onPatternColorChange(stripeId, patternId, slot, hex)
 */
export default function StripePanel({
    stripes, patterns, enabledIds, stripeConfigs, bodyColors, stripeDefaults,
    onToggle, onFillChange, onColorChange, onPatternColorChange,
}) {
    const [editingId, setEditingId] = useState(null);

    const effectiveId = enabledIds.includes(editingId)
        ? editingId
        : (enabledIds[enabledIds.length - 1] || null);
    const editing = stripes.find((s) => s.id === effectiveId) || null;

    const handleToggle = (id) => {
        if (!enabledIds.includes(id)) setEditingId(id); // edit what was just added
        onToggle(id);
    };

    if (stripes.length === 0) {
        return <div style={styles.note}>This garment has no stripe options.</div>;
    }

    return (
        <div style={styles.root}>
            <div>
                <div style={styles.sectionLabel}>Stripes — select any</div>
                <div style={styles.grid3}>
                    {stripes.map((s) => {
                        const isOn = enabledIds.includes(s.id);
                        return (
                            <button
                                key={s.id}
                                style={styles.optionBtn(isOn)}
                                onClick={() => handleToggle(s.id)}
                                title={isOn ? `Remove ${s.label}` : `Add ${s.label}`}
                            >
                                {isOn && <span style={styles.checkMark}>✓</span>}
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {enabledIds.length === 0 ? (
                <div style={styles.note}>No stripes selected.</div>
            ) : (
                <div>
                    {enabledIds.length > 1 && (
                        <div style={{ marginBottom: '10px' }}>
                            <div style={styles.sectionLabel}>Edit Fill For</div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {enabledIds.map((id) => {
                                    const s = stripes.find((x) => x.id === id);
                                    return (
                                        <button
                                            key={id}
                                            style={{ ...smallBtn(id === effectiveId), padding: '3px 8px' }}
                                            onClick={() => setEditingId(id)}
                                        >
                                            {s ? s.label : id}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {editing && (
                        <StripeEditor
                            key={editing.id}
                            stripe={editing}
                            config={stripeConfigs[editing.id] || {}}
                            patterns={patterns}
                            bodyColors={bodyColors}
                            stripeDefaults={stripeDefaults}
                            onFillChange={onFillChange}
                            onColorChange={onColorChange}
                            onPatternColorChange={onPatternColorChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
